/**
 * What a run session needs to play inside a party room (game/session.ts `net` option): the
 * room's frames for each local tick, snapshot catch-up, drift checks and the command uplink.
 *
 * The local sim never steps on its own here. Each local tick replays the room's next frame
 * (the exact commands the authoritative sim applied, bots included); with no frame queued
 * the tick stalls. Own commands go to the room at once and come back in a later frame, so
 * prediction.ts / localMotion.ts cover the round trip (see `leadMs`).
 */
import type { Command, Sim, Vec2, World } from '@keystone/sim';
import { TICK_MS } from '@keystone/sim';
import type { PartyClient } from './partyClient';
import type { OwnPredictor } from './predict';
import { type NetFrame, applyHandoffs, hashWorld } from './protocol';

/** Frames kept in reserve against network jitter; more than this is replayed at once. */
export const NET_BUFFER_FRAMES = 2;
/** Never draw the own hero further ahead of the replayed sim than this. */
export const MAX_LEAD_MS = 400;
const RESYNC_COOLDOWN_MS = 2000;
/** How fast the learned tick phase may drift later per frame (a slower link). */
const PHASE_DRIFT_MS = 0.5;

export interface NetSession {
  /** Controller per slot exactly as the room created the sim. */
  readonly controllers: readonly ('human' | 'bot')[];
  /** The frame for the tick about to run (snapshots restored first; hand-offs applied), or null to stall. */
  next(sim: Sim): NetFrame | null;
  /** After the step: compare the frame's checksum with ours (a mismatch asks for a snapshot). */
  check(frame: NetFrame, w: Readonly<World>): void;
  /** Send the local player's commands (repeats of the last identical move/face/target dropped). */
  flush(cmds: readonly Command[]): void;
  /** Frames waiting beyond the jitter reserve. */
  backlog(): number;
  /** How far ahead of the replayed sim to draw the own hero (ms): one round trip plus a tick. */
  leadMs(): number;
  /** Drift / gap recoveries so far (diagnostics). */
  readonly resyncs: number;
  /** Own-hero prediction (net/predict.ts); set by the session once its sims exist. */
  predictor: OwnPredictor | null;
  /** After each replayed tick: rewind the predictor to the confirmed world and replay unacked inputs. */
  predict(w: Readonly<World>): void;
  /** The confirmed world with the own unit as a command sent now will find it (for PlayerController.drain). */
  view(w: World): World;
  /** The own hero at the present + `offsetMs` (null: no prediction yet). */
  ownAt(offsetMs: number): Vec2 | null;
  /**
   * The room tick a command sent now lands on, fractional: the present the own hero is drawn at.
   * Learned from frame arrival times (the room's tick phase) and the measured round trip.
   */
  presentTick(): number | null;
}

export function netSession(party: PartyClient, controllers: readonly ('human' | 'bot')[], slot: number, now: () => number = () => performance.now()): NetSession {
  let lastResync = -Infinity;
  let resyncs = 0;
  let seq = 0;
  /** A's latest tick (the confirmed sim). */
  let aTick = 0;
  /**
   * Local time at which room tick 0's frame would arrive: min over (arrival - k * TICK_MS), so
   * network jitter (which only delays) doesn't move it; it may creep up PHASE_DRIFT_MS per frame
   * when the link gets slower.
   */
  let phase: number | null = null;
  let phaseK = -1;
  const notePhase = (): void => {
    for (const it of party.inbox) {
      if (it.t !== 'f' || it.k <= phaseK || it.at === undefined) continue;
      const s = it.at - it.k * TICK_MS;
      phase = phase === null ? s : Math.min(phase + PHASE_DRIFT_MS, s);
      phaseK = it.k;
    }
  };
  // A command sent at t reaches the room at t + up; room tick k runs at (frame arrival - down), so
  // it lands on the first k with k * TICK + phase >= t + up + down, i.e. ceil((t + rtt - phase) / TICK).
  // The minimum recent round trip: an estimate biased late would let a resend arrive a tick early
  // (e.g. while a dash still runs, where the sim ignores moves).
  const presentTick = (): number | null => (phase === null ? null : (now() + (party.rttMinMs ?? party.rttMs) - phase) / TICK_MS);
  const lead = (): number => {
    const p = presentTick();
    return Math.max(0, Math.min(MAX_LEAD_MS, p === null ? party.rttMs + TICK_MS : (p - aTick) * TICK_MS));
  };
  /** Last sent command per kind (move / face / target) and when. */
  const lastSent = new Map<string, { key: string; at: number }>();
  const resync = (why: string): void => {
    const t = now();
    if (t - lastResync < RESYNC_COOLDOWN_MS) return;
    lastResync = t;
    resyncs++;
    console.info(`[net] asking the room for a snapshot (${why})`);
    party.send({ t: 'resync' });
  };
  const self: NetSession = {
    controllers,
    predictor: null,
    get resyncs() {
      return resyncs;
    },
    next(sim) {
      const inbox = party.inbox;
      notePhase();
      while (inbox.length) {
        const it = inbox[0]!;
        if (it.t === 'snap') {
          inbox.shift();
          sim.restore(it.world);
          continue;
        }
        const tick = sim.getWorld().tick;
        if (it.k <= tick) {
          inbox.shift(); // covered by a snapshot
          continue;
        }
        if (it.k !== tick + 1) {
          // A gap (frames lost across a reconnect): ask for a snapshot, drop until it comes.
          resync(`gap: have tick ${tick}, got frame ${it.k}`);
          inbox.shift();
          continue;
        }
        inbox.shift();
        aTick = it.k;
        applyHandoffs(sim.getWorld() as World, it.x);
        const mine = it.a?.find(([s]) => s === slot);
        if (mine) self.predictor?.ack(mine[1]);
        return it;
      }
      return null;
    },
    check(frame, w) {
      if (frame.h !== undefined && hashWorld(w) !== frame.h) {
        console.warn(`[net] drift at tick ${frame.k}`);
        resync(`drift at tick ${frame.k}`);
      }
    },
    flush(cmds) {
      const out: Command[] = [];
      const t = now();
      for (const c of cmds) {
        if (c.type === 'move' || c.type === 'face' || c.type === 'target') {
          // With prediction the controller sees its own pending moves, so a repeat is a real resend
          // (after a dash); only same-tick doubles are dropped. Targets still lag: keep a round trip.
          const window = c.type === 'move' && self.predictor ? TICK_MS * 0.6 : party.rttMs + 150;
          const key = JSON.stringify(c);
          const last = lastSent.get(c.type);
          if (last && last.key === key && t - last.at < window) continue;
          lastSent.set(c.type, { key, at: t });
        }
        out.push(c);
      }
      if (!out.length) return;
      seq++;
      party.send({ t: 'cmd', c: out, s: seq });
      const p = presentTick();
      self.predictor?.record(seq, p === null ? aTick + 1 + Math.ceil(lead() / TICK_MS) : Math.ceil(p), out);
    },
    backlog() {
      return Math.max(0, party.inbox.length - NET_BUFFER_FRAMES);
    },
    leadMs: lead,
    predict(w) {
      aTick = w.tick;
      const p = presentTick() ?? w.tick + lead() / TICK_MS;
      self.predictor?.update(w, Math.ceil(p), p);
    },
    presentTick,
    view(w) {
      return self.predictor ? self.predictor.view(w) : w;
    },
    ownAt(offsetMs) {
      const p = presentTick();
      return p === null ? null : (self.predictor?.sample(p + offsetMs / TICK_MS) ?? null);
    },
  };
  return self;
}
